import React from 'react'
import { motion } from 'framer-motion'
import { RefreshCw, Users, Search, Shield, Zap, Share2 } from 'lucide-react'

const FeaturesSection: React.FC = () => {

  const features = [
    {
      icon: RefreshCw,
      title: "Cross-Device Sync",
      description: "Save sessions on your desktop and instantly access them on mobile. Cloud sync keeps your browsing workflow connected across all devices.",
      color: "green",
      bgColor: "bg-green-100",
      textColor: "text-green-600",
      showOnMobile: true
    },
    {
      icon: Search,
      title: "Instant Search & Discovery",
      description: "AI-powered search finds any session in milliseconds. Smart filters and tags help you discover exactly what you need, when you need it.",
      color: "yellow",
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-600",
      showOnMobile: true
    },
    {
      icon: Share2,
      title: "One-Click Session Sharing",
      description: "Share complete browsing contexts with teammates. Include tabs, notes, and research - perfect for collaborative projects and knowledge transfer.",
      color: "blue",
      bgColor: "bg-blue-100",
      textColor: "text-blue-600",
      showOnMobile: true
    },
    {
      icon: Users,
      title: "Team Collaboration Hub",
      description: "Create shared workspaces for project research. Team members can contribute sessions, add context, and build knowledge bases together.",
      color: "orange",
      bgColor: "bg-orange-100",
      textColor: "text-orange-600",
      showOnMobile: true
    },
    {
      icon: Shield,
      title: "Privacy-First Architecture",
      description: "End-to-end encryption protects your data. Choose local-only storage or secure cloud sync - full control over your browsing privacy.",
      color: "gray",
      bgColor: "bg-gray-100",
      textColor: "text-gray-600",
      showOnMobile: false
    },
    {
      icon: Zap,
      title: "AI-Powered Organization",
      description: "Smart categorization automatically groups related tabs. Machine learning adapts to your browsing patterns for intelligent session management.",
      color: "purple",
      bgColor: "bg-purple-100",
      textColor: "text-purple-600",
      showOnMobile: false
    }
  ]

  return (
    <section id="features" className="py-6 sm:py-8 md:py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          className="text-center mb-8 sm:mb-12"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          viewport={{ once: true, margin: "-50px" }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium text-gray-900 mb-4 sm:mb-6">
            Powerful Features for Modern Browsing
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            AI-powered session management, seamless collaboration, and privacy-first design - everything you need to transform your browsing workflow
          </p>
        </motion.div>

        {/* Mobile-Optimized Grid Layout - Single Column on Mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className={`bg-white rounded-lg sm:rounded-xl p-4 sm:p-5 md:p-6 border-2 border-gray-200 hover:border-blue-600 transition-all duration-300 hover:shadow-lg ${
                !feature.showOnMobile ? 'hidden md:block' : ''
              }`}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.25, 
                delay: index * 0.05, 
                ease: "easeOut"
              }}
              viewport={{ once: true, margin: "-20px" }}
              whileHover={{ 
                y: -4,
                transition: { duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }
              }}
            >
              <motion.div 
                className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 ${feature.bgColor} rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 md:mb-5`}
                whileHover={{ 
                  scale: 1.1,
                  transition: { duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }
                }}
              >
                <feature.icon className={`w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 ${feature.textColor}`} />
              </motion.div>
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 leading-tight">{feature.title}</h3>
              <p className="text-gray-600 text-xs sm:text-sm md:text-base leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default FeaturesSection